import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { ClaimStatusTypes } from "@enums/claim-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedServiceAssertionPerformance } from "@idw-types/data/reports/service-assertion-performance";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./service-assertion-performance-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type ServiceAssertionPerformanceTableContainerProps = {
    handleClickOnTableCellLink: (claimStatusType: ClaimStatusTypes, serviceAssertionPerformance: FormattedServiceAssertionPerformance) => void;
    serviceAssertionPerformances: Array<FormattedServiceAssertionPerformance>;
    passGreenRange: Array<number>;
    passYellowRange: Array<number>;
    passRedRange: Array<number>;
    unverifiedGreenRange: Array<number>;
    unverifiedYellowRange: Array<number>;
    unverifiedRedRange: Array<number>;
    failGreenRange: Array<number>;
    failYellowRange: Array<number>;
    failRedRange: Array<number>;
    tableState: TableState;
};

type ServiceAssertionPerformanceTableDataRow = FormattedServiceAssertionPerformance & {
    assertionShortName: string;
    serviceOfferingName: string;
};

export const ServiceAssertionPerformanceTableContainer = (props: ServiceAssertionPerformanceTableContainerProps) => {
    const {
        handleClickOnTableCellLink,
        serviceAssertionPerformances,
        passGreenRange,
        passRedRange,
        passYellowRange,
        failGreenRange,
        failRedRange,
        failYellowRange,
        unverifiedGreenRange,
        unverifiedRedRange,
        unverifiedYellowRange,
        tableState
    } = props;

    const dispatch = useDispatch();

    const serviceAssertionPerformanceTableData: Array<ServiceAssertionPerformanceTableDataRow> = useMemo(() => {
        return serviceAssertionPerformances.map(sap => ({
            ...sap,
            assertionShortName: sap.apAssertion?.assertion?.shortName || "",
            serviceOfferingName: sap.apAssertion?.serviceOffering?.name || ""
        }));
    }, [serviceAssertionPerformances]);

    const columnHelper = createColumnHelper<ServiceAssertionPerformanceTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("assertionShortName", {
            header: resources.tables.serviceAssertionPerformance.columns.assertionName.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("serviceOfferingName", {
            id: "sap-service",
            header: resources.tables.serviceAssertionPerformance.columns.service.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("passAssertionPercent", {
            header: resources.tables.serviceAssertionPerformance.columns.pass.header,
            cell: (props) => {
                const value = props.getValue();
                if (value) {
                    let className = "";
                    if (value >= passGreenRange[0] && value <= passGreenRange[1]) {
                        className = styles.greenBackground;
                    } else if (value >= passYellowRange[0] && value <= passYellowRange[1]) {
                        className = styles.yellowBackground;
                    } else if (value >= passRedRange[0] && value <= passRedRange[1]) {
                        className = styles.redBackground;
                    }
                    return (
                        <span id={`pass-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.PASS, props.row.original)} className={className}>
                            {value}
                        </span>
                    );
                } else {
                    return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("closeAssertionPercent", {
            header: resources.tables.serviceAssertionPerformance.columns.close.header,
            cell: (props) => props.getValue() ? <span>{props.getValue()}</span> : <span>{resources.common.notFound}</span>
        }),
        columnHelper.accessor("partialAssertionPercent", {
            header: resources.tables.serviceAssertionPerformance.columns.partial.header,
            cell: (props) => props.getValue() ? <span>{props.getValue()}</span> : <span>{resources.common.notFound}</span>
        }),
        columnHelper.accessor("unverifiedAssertionPercent", {
            header: resources.tables.serviceAssertionPerformance.columns.unverified.header,
            cell: (props) => {
                const value = props.getValue();
                if (value) {
                    let className = "";
                    if (value >= unverifiedGreenRange[0] && value <= unverifiedGreenRange[1]) {
                        className = styles.greenBackground;
                    } else if (value >= unverifiedYellowRange[0] && value <= unverifiedYellowRange[1]) {
                        className = styles.yellowBackground;
                    } else if (value >= unverifiedRedRange[0] && value <= unverifiedRedRange[1]) {
                        className = styles.redBackground;
                    }
                    return (
                        <span id={`unverified-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.UNVERIFIED, props.row.original)} className={className}>
                            {value}
                        </span>
                    );
                } else {
                    return <span>{resources.common.notFound}</span>
                }
            }
        }),
        columnHelper.accessor("failAssertionPercent", {
            header: resources.tables.serviceAssertionPerformance.columns.fail.header,
            cell: (props) => {
                const value = props.getValue();
                if (value) {
                    let className = "";
                    if (value >= failGreenRange[0] && value <= failGreenRange[1]) {
                        className = styles.greenBackground;
                    } else if (value >= failYellowRange[0] && value <= failYellowRange[1]) {
                        className = styles.yellowBackground;
                    } else if (value >= failRedRange[0] && value <= failRedRange[1]) {
                        className = styles.redBackground;
                    }
                    return (
                        <span id={`fail-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.FAIL, props.row.original)} className={className}>
                            {value}
                        </span>
                    );
                } else {
                    return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("passAssertionScorePoints", {
            header: resources.tables.serviceAssertionPerformance.columns.assertionScorePoints.header,
            cell: (props) => props.getValue() ? <span>{props.getValue()}</span> : <span>{resources.common.notFound}</span>
        }),
        columnHelper.accessor("failTrustScoreModifier", {
            header: resources.tables.serviceAssertionPerformance.columns.trustScorePenalty.header,
            cell: (props) => props.getValue() ? <span>{props.getValue()}</span> : <span>{resources.common.notFound}</span>
        })
    ]), [serviceAssertionPerformanceTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: serviceAssertionPerformanceTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.serviceAssertionPerformanceTableContainer}>
            <IdwTable
                id="service-assertion-performance"
                className={styles.serviceAssertionPerformanceTable}
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"service-assertion-performance"}
            />
            <PaginationContainer
                idPrefix="service-assertion-performance"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};